import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCreateApp, type CreateAppResult } from '@/lib/queries';
import { useContextStore } from '@/stores/context';
import { Smartphone } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

/** Create an app. The server also mints its environments + a production write key, returned once —
 *  the parent gets it via `onCreated` to drive the reveal-once + install guide. */
export function AddAppDialog({
  trigger,
  onCreated,
}: {
  trigger: React.ReactNode;
  onCreated: (result: CreateAppResult) => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const create = useCreateApp();
  const { setApp, setEnvironment } = useContextStore();

  const submit = (): void => {
    if (!name.trim()) return;
    create.mutate(
      { name: name.trim(), platform: 'ios' },
      {
        onSuccess: (result) => {
          // Make the new app the active context so every app-scoped page follows the user here.
          setApp(result.app.id);
          setEnvironment(result.environment_ids.production);
          setOpen(false);
          setName('');
          onCreated(result);
        },
        onError: (error: Error) => toast.error(error.message),
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create an app</DialogTitle>
          <DialogDescription>
            Connect the iOS app where you’ll deliver experiences. We’ll create staging and production environments for you.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-1">
          <div className="space-y-1.5">
            <Label htmlFor="app-name">App name</Label>
            <Input
              id="app-name"
              aria-label="app name"
              autoFocus
              maxLength={200}
              placeholder="e.g. Acme"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && submit()}
            />
            <p className="text-xs text-muted-foreground">Use the name your team will recognize in the dashboard.</p>
          </div>

          <div className="flex items-center gap-3 rounded-lg border bg-secondary/30 p-3">
            <span className="grid size-9 shrink-0 place-items-center rounded-md bg-background text-indigo-600 shadow-sm ring-1 ring-border dark:text-indigo-400">
              <Smartphone className="size-4" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">iOS</p>
              <p className="text-xs text-muted-foreground">Swift Package Manager · iOS 17+</p>
            </div>
            <span className="rounded-full bg-indigo-500/10 px-2 py-1 text-[10px] font-semibold text-indigo-600 dark:text-indigo-300">
              Available
            </span>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button disabled={!name.trim() || create.isPending} onClick={submit}>
            {create.isPending ? 'Creating…' : 'Create app'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
